// src/components/Cart.js
import { useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import { UserContext } from '../context/UserContext';
import { OrderContext } from '../context/OrderContext';
import '../css/Cart.css';
import { useLanguage } from '../context/LanguageContext';
import { groupByZone, getZoneColor, normalizeZone } from './cartZones';
import CartLines, { isEditableProfile } from './CartLines';
import CartDialog from './CartDialog';
import userService from '../services/userService';
import '../css/CartPage.css';

// Importi in formato italiano col simbolo, come nel PDF del preventivo (es. € 1.586,25).
const fmtEur = (value) =>
  `€ ${Number(value || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Aliquota IVA mostrata nel riepilogo: stessa regola del server (backend/routes/orderRoutes.js, computeVatRate).
const vatCountryOf = (vat) => {
  const compact = String(vat || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  const prefix = compact.slice(0, 2);
  return /^[A-Z]{2}$/.test(prefix) ? prefix : '';
};
const BUSINESS_ROLES = ['azienda', 'agenzia', 'elettricista', 'architetto'];
const computeVatRate = ({ recipient, customerCountry, userRole, userCompanyName }) => {
  const recipientVatCountry = vatCountryOf(recipient?.vat);
  const hasBusinessContext =
    !!String(recipient?.company || userCompanyName || '').trim() ||
    BUSINESS_ROLES.includes(String(userRole || '').toLowerCase());
  const country = String(customerCountry || '').trim().toUpperCase();
  const isForeignBusiness =
    hasBusinessContext && ((recipientVatCountry && recipientVatCountry !== 'IT') || (country && country !== 'IT'));
  return isForeignBusiness ? 0 : 22;
};

const Cart = () => {
  const { t } = useLanguage();
  const DRAFT_META_KEY = 'cartDraftMeta_v1';

  const readDraftMeta = () => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = localStorage.getItem(DRAFT_META_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const writeDraftMeta = (meta) => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(DRAFT_META_KEY, JSON.stringify(meta));
    } catch {
      /* ignore */
    }
  };

  const clearDraftMeta = () => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(DRAFT_META_KEY);
  };

  // ATTENZIONE: se nel tuo CartContext usa un nome diverso per la rimozione, rinomina removeFromCart qui
  const {
    cart,
    addToCart,
    updateCartItem,
    updateCartItemNotes,
    removeFromCart,   // ← ok, qui la virgola è permessa perché NON è un rest element
    clearCart,
    currentOrder,
    moveCartItem, //
    moveZone, 
  } = useContext(CartContext);

  const { placeOrder, updateOrder, updateOrderRecipient } = useContext(OrderContext);
  const { user: userDetails } = useContext(UserContext);
  const { user: authUser, setUser: setAuthUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Ritorno da "Modifica" nel configuratore: la riga aggiornata si illumina un attimo.
  const [highlightId, setHighlightId] = useState(() => location.state?.updatedId ?? null);
  useEffect(() => {
    if (highlightId == null) return undefined;
    navigate(location.pathname, { replace: true, state: null });
    const timer = setTimeout(() => setHighlightId(null), 2500);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightId]);

  const draftMeta = (!currentOrder ? readDraftMeta() : null) || {};

  // Stati ordine
  const [orderReference, setOrderReference] = useState(
    currentOrder?.reference || draftMeta.reference || ''
  );
  const [orderNotes, setOrderNotes] = useState(
    currentOrder?.notes || draftMeta.notes || ''
  );
  const [message, setMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // L'utente può gestire il destinatario se è stato abilitato
  // alla preventivazione tramite quoteEnabled (o se admin).
  const canManageRecipient =
    !!authUser &&
    (authUser.role === 'admin' || !!authUser.quoteEnabled);
  const canManageQuoteCommercials = canManageRecipient;

  const paymentMethodOptions = [
    { value: 'bank_transfer', label: 'Bonifico bancario' },
    { value: 'riba_30', label: 'Ri.Ba. 30gg' },
    { value: 'riba_60', label: 'Ri.Ba. 60gg' },
    { value: 'riba_90', label: 'Ri.Ba. 90gg' },
    { value: 'cash', label: 'Contanti' },
    { value: 'card', label: 'Carta' },
    { value: 'other', label: 'Altro' },
  ];

  // Destinatario del preventivo/ordine (solo per ruoli abilitati)
  const [recipient, setRecipient] = useState(() => {
    const base = currentOrder?.recipient || {};
    const draftRecipient = draftMeta.recipient || {};
    return {
      recipientType:
        base.recipientType ||
        draftRecipient.recipientType ||
        (canManageRecipient ? 'lead' : 'customer'),
      name: base.name || draftRecipient.name || '',
      company: base.company || draftRecipient.company || '',
      email: base.email || draftRecipient.email || '',
      phone: base.phone || draftRecipient.phone || '',
      address: base.address || draftRecipient.address || '',
      vat: base.vat || draftRecipient.vat || '',
    };
  });
  const [payment, setPayment] = useState(() => {
    const base = currentOrder?.payment || {};
    const draftPayment = draftMeta.payment || {};
    return {
      method: base.method || draftPayment.method || 'bank_transfer',
      terms: base.terms || draftPayment.terms || '',
      dueDays: base.dueDays ?? draftPayment.dueDays ?? '',
      depositPercent: base.depositPercent ?? draftPayment.depositPercent ?? '',
      deliveryTime: base.deliveryTime || draftPayment.deliveryTime || '',
      notes: base.notes || draftPayment.notes || '',
    };
  });

  // Colori delle zone (le righe sono raggruppate per zona in CartLines e nel salvataggio).
  const zoneColorOf = getZoneColor(cart);



    // Rubrica → scelta destinatario da addressBook
  const [showRecipientPicker, setShowRecipientPicker] = useState(false);
  const [recipientSearch, setRecipientSearch] = useState('');

  // Rubrica dell'utente loggato (arriva da AuthContext -> user.addressBook)
  const addressBook = authUser?.addressBook || [];

  const filteredAddressBook = addressBook.filter((c) =>
    (c.name + ' ' + c.company + ' ' + c.email + ' ' + c.phone + ' ' + c.vat)
      .toLowerCase()
      .includes(recipientSearch.toLowerCase())
  );

  // Errori per evidenziare i campi obbligatori
  const [fieldErrors, setFieldErrors] = useState({});
  // Scheda destinatario (desktop): compatta quando nome e azienda ci sono; true = modulo aperto da "Modifica"
  const [recipientEditing, setRecipientEditing] = useState(false);
  // "Salva in rubrica": stato del salvataggio del destinatario come nuovo contatto
  const [contactSave, setContactSave] = useState({ status: 'idle', message: '' });
  // Se il destinatario cambia dopo il salvataggio, torna disponibile "Salva in rubrica"
  useEffect(() => {
    setContactSave((prev) => (prev.status === 'saving' ? prev : { status: 'idle', message: '' }));
  }, [recipient.name, recipient.company, recipient.vat, recipient.email]);
  const recipientFilled = !!(String(recipient.name || '').trim() && String(recipient.company || '').trim());
  // Destinatario incompleto (es. nuovo preventivo): il modulo resta aperto finché non si preme "Fatto",
  // così non si chiude a metà mentre si scrive a mano.
  useEffect(() => {
    if (!recipientFilled) setRecipientEditing(true);
  }, [recipientFilled]);

  const handlePickRecipient = (contact) => {
    setRecipient((prev) => ({
      ...prev,
      recipientType: 'lead',
      refId: contact.id || null,
      name: contact.name || '',
      company: contact.company || '',
      email: contact.email || '',
      phone: contact.phone || '',
      address: contact.address || '',
      vat: contact.vat || '',
    }));
    // Condizioni pagamento già note dalla rubrica (codice/descrizione ERP): niente da ridigitare.
    if (canManageQuoteCommercials && contact.codpagd) {
      setPayment((prev) => ({ ...prev, terms: contact.codpagd }));
    }
    setShowRecipientPicker(false);
    setRecipientEditing(false); // scelto dalla rubrica: torna al riepilogo compatto
  };


  

// Riconosce una riga "abbuono" anche se non ha il type settato
const isAdjustmentLike = (item) =>
  item?.type === 'adjustment' ||
  String(item?.code || '').toUpperCase() === t('cart.adjustmentCode') ||
  Number(item?.unitPrice) < 0;

// Forza i campi corretti sugli abbuoni (type, discount, quantity)
const normalizeAdjustments = () => {
  let changed = false;
  (cart || []).forEach((it) => {
    if (!isAdjustmentLike(it)) return;

    if (it.type !== 'adjustment') {
      updateCartItem(it.id, 'type', 'adjustment');
      changed = true;
    }
    if (Number(it.discount) !== 0) {
      updateCartItem(it.id, 'discount', 0);
      changed = true;
    }
    if (Number(it.quantity) !== 1) {
      updateCartItem(it.id, 'quantity', 1);
      changed = true;
    }
  });
  return changed;
};

  // ===================== Helpers di calcolo (gestiscono abbuono) =====================
  const getRowDiscount = (item) => {
  if (!item) return 0;

  // Abbuoni = sconto non modificabile
  if (item.type === 'adjustment') return 0;

  // Se la riga ha un proprio sconto salvato, usa quello
  if (
    Object.prototype.hasOwnProperty.call(item, 'discount') &&
    item.discount !== null &&
    item.discount !== '' &&
    !isNaN(Number(item.discount))
  ) {
    return Number(item.discount);
  }

  // Default: sconto profilo utente
  return userDetails?.discount ?? 0;
};

  const getNetUnit = (item) => {
    const disc = getRowDiscount(item);
    return Number(item.unitPrice || 0) * (1 - Number(disc) / 100);
  };
  const getRowTotal = (item) => getNetUnit(item) * Number(item.quantity || 0);

  const updateCartTotal = () => {
    const total = cart.reduce((sum, it) => sum + getRowTotal(it), 0);
    return Number.isFinite(total) ? total.toFixed(2) : '0.00';
  };

  // ===================== Aggiungi riga VUOTA (non a catalogo) =====================
  // zone (facoltativa): la riga nasce già nella zona da cui è stata aggiunta.
  const addEmptyRow = (zone) => {
    const newItem = {
      id: Date.now(),
      type: 'manual',          // normale riga manuale
      code: '',
      description: '',
      unitPrice: 0,
      quantity: 1,
      discount: getRowDiscount({}),
      notes: '',
      manual: true,
      ...(typeof zone === 'string' && normalizeZone(zone) ? { zone: normalizeZone(zone) } : {}),
    };
    addToCart(newItem);
  };

  // "Modifica": riapre la riga nel configuratore (Profili su misura) o nella scheda del Catalogo
  // (prodotti con varianti: colore luce, finitura, ...), che poi la sostituiscono al suo posto.
  const editRow = (item) => navigate(isEditableProfile(item) ? '/configurator' : '/catalogo', { state: { editItem: item } });

  // Duplica una riga subito sotto l'originale, con lo stesso sconto di riga.
  const duplicateRow = (item) => {
    const copy = { ...item, id: Date.now() + Math.random(), position: (item.position ?? 0) + 0.5 };
    delete copy._id;
    addToCart(copy);
    updateCartItem(copy.id, 'discount', getRowDiscount(item)); // addToCart reimposta lo sconto del cliente
  };

  // ===================== Aggiungi riga Abbuono (importo NEGATIVO) =====================
  // Modulo nella pagina (CartDialog) per abbuono e nome zona, al posto dei popup del browser.
  const [dialog, setDialog] = useState(null);

  const createAdjustmentRow = (amount, reason) => {
    const newItem = {
      id: Date.now(),
      type: 'adjustment',
      code: t('cart.adjustmentCode'),
      description: reason || t('cart.adjustmentDescription'),
      unitPrice: -Math.abs(amount), // IMPORTO NEGATIVO
      quantity: 1,                  // sempre 1
      discount: 0,                  // niente sconto su abbuono
      notes: reason || '',
      manual: true,
    };
    addToCart(newItem);
  };

  const addAdjustmentRow = () => {
    setDialog({
      title: t('cart.addAdjustment'),
      confirmLabel: t('cart.addAdjustment'),
      fields: [
        { id: 'amount', label: t('cart.promptAdjustmentAmount'), type: 'number', step: '0.01', defaultValue: '' },
        { id: 'reason', label: t('cart.promptAdjustmentReason'), defaultValue: t('cart.adjustmentDefaultReason') },
      ],
      onConfirm: ({ amount, reason }) => {
        const value = parseFloat(String(amount).replace(',', '.'));
        if (isNaN(value) || value <= 0) return t('cart.alertInvalidAdjustmentAmount');
        createAdjustmentRow(value, String(reason || '').trim());
        return null;
      },
    });
  };

  const requestZoneName = (onName) => {
    setDialog({
      title: t('cart.newZone'),
      confirmLabel: t('cart.ok'),
      fields: [{ id: 'name', label: t('cart.zonePrompt'), maxLength: 100, defaultValue: '' }],
      onConfirm: ({ name }) => {
        if (!String(name || '').trim()) return t('cart.zoneNameMissing');
        onName(name);
        return null;
      },
    });
  };

  // Torna alla lista ordini (in modifica chiude l'ordine, come prima)
  const backToOrders = () => {
    if (currentOrder) {
      clearCart();
      clearDraftMeta();
    }
    navigate('/orders');
  };

  // ===================== Validazione =====================
  const validateOrder = () => {
    const newErrors = {};

    if (!cart.length) {
      newErrors.cart = t('cart.validationCartEmpty');
    }

    cart.forEach((item, index) => {
      if (item.type === 'adjustment') {
        if (item.unitPrice >= 0) {
          newErrors[`itemUnitPrice${index}`] = t('cart.validationAdjustmentNegative');
        }
        if (Number(item.quantity) !== 1) {
          newErrors[`itemQuantity${index}`] = t('cart.validationAdjustmentQty');
        }
      } else {
        if (!item.code || !String(item.code).trim()) {
          newErrors[`itemCode${index}`] = t('cart.validationCodeMissing');
        }
        if (!item.description || !String(item.description).trim()) {
          newErrors[`itemDescription${index}`] = t('cart.validationDescriptionMissing');
        }
        if (Number(item.unitPrice) <= 0) {
          newErrors[`itemUnitPrice${index}`] = t('cart.validationPricePositive');
        }
        if (Number(item.quantity) <= 0) {
          newErrors[`itemQuantity${index}`] = t('cart.validationQtyPositive');
        }
        if (canManageRecipient) {
          const hasName = recipient.name && recipient.name.trim();
          const hasCompany = recipient.company && recipient.company.trim();
          if (!hasName || !hasCompany) {
            newErrors.recipient = t('cart.validationRecipientMissing');
          }
        }

      }
    });

    // Riferimento sempre obbligatorio
    if (!orderReference || !orderReference.trim()) {
      newErrors.reference = t('cart.validationReferenceMissing');
    }

    if (canManageQuoteCommercials) {
      if (!payment.method) {
        newErrors.paymentMethod = 'Seleziona una modalita di pagamento.';
      }
      if (!payment.terms || !String(payment.terms).trim()) {
        newErrors.paymentTerms = 'Inserisci le condizioni di pagamento.';
      }
    }

    const valid = Object.keys(newErrors).length === 0;
    setFieldErrors(newErrors);
    return { valid, errors: newErrors };
  };

  const handleDiscountChange = (id, value) => {
  const item = cart.find((i) => i.id === id);
  if (!item) return;
  if (item.type === 'adjustment') return; // Niente sconto sugli abbuoni

  const num = parseFloat(String(value).replace(',', '.'));

  if (isNaN(num)) {
    // se svuoto il campo → ritorna allo sconto del profilo
    updateCartItem(id, 'discount', '');
    return;
  }

  const clamped = Math.max(0, Math.min(100, num)); // evita 999 o negativi
  updateCartItem(id, 'discount', clamped);
};


  // ===================== Handlers UI =====================
  const handleQuantityChange = (id, quantity) => {
    const parsedQuantity = parseInt(quantity, 10);
    const currentItem = cart.find((i) => i.id === id);
    if (currentItem?.type === 'adjustment') return; // qty fissa a 1 per abbuono
    if (parsedQuantity > 0) {
      updateCartItem(id, 'quantity', parsedQuantity);
    }
  };

  const handlePriceChange = (id, value) => {
    if (!canManageRecipient) return; // listino bloccato per chi non è abilitato ai preventivi
    const num = parseFloat(String(value));
    const item = cart.find((i) => i.id === id);
    if (!item) return;
    // Prezzo negativo permesso SOLO per abbuono
    if (item.type !== 'adjustment' && num < 0) return;
    updateCartItem(id, 'unitPrice', isNaN(num) ? 0 : num);
  };

  const handleRemoveItem = (id) => {
    if (window.confirm(t('cart.confirmRemoveRow'))) {
      removeFromCart(id);
    }
  };

  const handleClearCart = () => {
    if (window.confirm(t('cart.confirmClearCart'))) {
      clearCart();
      clearDraftMeta();
    }
  };
  console.log('[CART] cart items:', cart);
  console.log(
  '[CART] datasheet by code:',
  cart.map(i => ({
    code: i.code,
    datasheet: i.datasheet,
  }))
);

  // ===================== Salva / Invia ordine =====================
   const buildOrderPayload = () => {
    const total = Number(updateCartTotal());

    const base = {
      orderNumber: currentOrder ? currentOrder.orderNumber : undefined, // assegnato dal backend
      ...(currentOrder && { _id: currentOrder._id }),
      // Salvate nell'ordine visualizzato (raggruppate per zona): il PDF le stampa così.
      items: groupByZone(cart).map((item) => ({
        ...item,
        discount: getRowDiscount(item), // sconto 0 per abbuono, sconto profilo per le altre righe
      })),
      total,
      date: new Date(),
      reference: orderReference,
      status: currentOrder ? currentOrder.status : 'preparato',
      notes: orderNotes,
    };

    // Per i NUOVI ordini, se l'utente può gestire i preventivi, spediamo anche il destinatario
    if (!currentOrder && canManageRecipient) {
      base.recipient = recipient;
    }

    if (canManageQuoteCommercials) {
      base.payment = {
        method: payment.method || '',
        terms: payment.terms || '',
        dueDays: payment.dueDays === '' ? null : Number(payment.dueDays),
        depositPercent: payment.depositPercent === '' ? null : Number(payment.depositPercent),
        deliveryTime: payment.deliveryTime || '',
        notes: payment.notes || '',
      };
    }

    return base;
  };


const confirmPlaceOrder = async () => {
  const { valid, errors } = validateOrder();
  if (!valid) {
    console.warn('Validation errors:', errors);
    alert(t('cart.validationCheck'));
    return;
  }

  setIsSaving(true);
  try {
    const order = buildOrderPayload();

    if (currentOrder) {
      // Modifica ordine esistente: prima aggiorniamo righe/valori
      await updateOrder(order);

      // Se l'utente è abilitato alla funzione preventivi, aggiorniamo anche il destinatario
      if (canManageRecipient) {
        const old = currentOrder.recipient || {};
        const hasChanged = [
          'recipientType',
          'name',
          'company',
          'email',
          'phone',
          'address',
          'vat',
        ].some((key) => (recipient[key] || '') !== (old[key] || ''));

        if (hasChanged) {
          try {
            await updateOrderRecipient(currentOrder._id, recipient);
          } catch (err) {
            console.error('Errore durante l\'aggiornamento del destinatario:', err);
            // opzionale: alert dedicato
          }
        }
      }

      clearCart();            // ← svuota anche dopo AGGIORNA
      clearDraftMeta();
      setOrderReference('');  // opzionale
      setOrderNotes('');      // opzionale
      setMessage(t('cart.orderUpdated'));
    } else {
      // Nuovo ordine
      await placeOrder(order);
      clearCart();            // ← svuota dopo SALVA
      clearDraftMeta();
      setOrderReference('');
      setOrderNotes('');
      setMessage(t('cart.orderSaved'));
    }

    navigate('/orders');
  } catch (err) {
    console.error(err);
    alert(t('cart.saveError'));
  } finally {
    setIsSaving(false);
  }
};



  // ===================== Effetti =====================
  useEffect(() => {
    if (currentOrder) {
      setOrderReference(currentOrder.reference || '');
      setOrderNotes(currentOrder.notes || '');
    }
  }, [currentOrder]);

  useEffect(() => {
    const base = currentOrder?.recipient || {};
    const draftRecipient = !currentOrder && canManageRecipient ? readDraftMeta()?.recipient || {} : {};
    setRecipient({
      recipientType:
        base.recipientType ||
        draftRecipient.recipientType ||
        (canManageRecipient ? 'lead' : 'customer'),
      name: base.name || draftRecipient.name || '',
      company: base.company || draftRecipient.company || '',
      email: base.email || draftRecipient.email || '',
      phone: base.phone || draftRecipient.phone || '',
      address: base.address || draftRecipient.address || '',
      vat: base.vat || draftRecipient.vat || '',
    });
  }, [currentOrder, canManageRecipient]);

  useEffect(() => {
    const basePayment = currentOrder?.payment || {};
    const draftPayment = !currentOrder && canManageQuoteCommercials ? readDraftMeta()?.payment || {} : {};

    setPayment({
      method: basePayment.method || draftPayment.method || 'bank_transfer',
      terms: basePayment.terms || draftPayment.terms || '',
      dueDays: basePayment.dueDays ?? draftPayment.dueDays ?? '',
      depositPercent: basePayment.depositPercent ?? draftPayment.depositPercent ?? '',
      deliveryTime: basePayment.deliveryTime || draftPayment.deliveryTime || '',
      notes: basePayment.notes || draftPayment.notes || '',
    });
  }, [currentOrder, canManageQuoteCommercials]);


  // Quando il carrello cambia (es. carico un ordine in modifica), rimetti a posto gli abbuoni
useEffect(() => {
  normalizeAdjustments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [cart]);

  useEffect(() => {
    if (currentOrder) {
      clearDraftMeta();
      return;
    }
    const meta = {
      reference: orderReference,
      notes: orderNotes,
    };
    if (canManageRecipient) {
      meta.recipient = recipient;
    }
    if (canManageQuoteCommercials) {
      meta.payment = payment;
    }
    writeDraftMeta(meta);
  }, [orderReference, orderNotes, recipient, payment, canManageRecipient, canManageQuoteCommercials, currentOrder]);


 

  // ===================== Salva destinatario in rubrica =====================
  const sameText = (a, b) => !!String(a || '').trim() && String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
  // Già in rubrica: stesso contatto scelto, oppure stessa P.IVA, stessa email o stessi azienda + nome
  const recipientInAddressBook = addressBook.some((c) =>
    (recipient.refId && c.id === recipient.refId) ||
    sameText(c.vat, recipient.vat) ||
    sameText(c.email, recipient.email) ||
    (sameText(c.company, recipient.company) && sameText(c.name, recipient.name))
  );
  const saveRecipientToAddressBook = async () => {
    setContactSave({ status: 'saving', message: '' });
    try {
      const contact = await userService.addAddressBookContact({
        name: recipient.name,
        company: recipient.company,
        email: recipient.email,
        phone: recipient.phone,
        vat: recipient.vat,
        address: recipient.address,
        // condizioni di pagamento: tornano compilate quando lo si riseleziona dalla rubrica
        ...(canManageQuoteCommercials && payment.terms ? { codpagd: payment.terms } : {}),
      });
      const updated = await userService.getAddressBook();
      setAuthUser((prev) => (prev ? { ...prev, addressBook: updated } : prev));
      if (contact?.id) setRecipient((prev) => ({ ...prev, refId: contact.id }));
      setContactSave({ status: 'saved', message: t('cart.savedToAddressBook') });
    } catch (err) {
      console.error('Errore salvataggio in rubrica:', err);
      setContactSave({ status: 'error', message: t('cart.saveToAddressBookError') });
    }
  };
  const saveToAddressBookControl = recipientFilled && (!recipientInAddressBook || contactSave.status === 'saved') && (
    <div className="c2-save-contact">
      {contactSave.status === 'saved' ? (
        <span className="c2-save-ok">✓ {contactSave.message}</span>
      ) : (
        <button type="button" className="c2-link c2-link-start" disabled={contactSave.status === 'saving'} onClick={saveRecipientToAddressBook}>
          {contactSave.status === 'saving' ? t('cart.savingToAddressBook') : `+ ${t('cart.saveToAddressBook')}`}
        </button>
      )}
      {contactSave.status === 'error' && <span className="c2-save-err">{contactSave.message}</span>}
    </div>
  );

  // ===================== Blocchi riusati (telefono e pannello desktop) =====================
  const orderNotesField = (
    <div className="field c2-notes">
      <label>{t('cart.orderNotes')}</label>
      <textarea
        value={orderNotes}
        onChange={(e) => setOrderNotes(e.target.value)}
        rows={2}
      />
    </div>
  );
  const recipientBlock = canManageRecipient && (
  <div className="field-group">
     <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 8,
      }}
    >
      <h4 style={{ margin: 0 }}>{t('cart.recipientQuote')}</h4>

      {addressBook.length > 0 && (
        <button
          type="button"
         className="cart-button secondary"
  onClick={() => setShowRecipientPicker(true)}
  style={{ padding: '5px 10px', fontSize: 14.4 }}
        >
          {t('cart.pickFromAddressBook')}
        </button>
      )}
    </div>

    {/* Riga 1: nome + azienda */}
    <div className="field-row">
      <div className="field">
        <label>{t('cart.contactName')}</label>
        <input
          type="text"
          value={recipient.name}
          onChange={(e) =>
            setRecipient((prev) => {
              const next = { ...prev, name: e.target.value };
              if (fieldErrors.recipient) {
                setFieldErrors((prevErr) => ({ ...prevErr, recipient: undefined }));
              }
              return next;
            })
          }
          placeholder="Es. Mario Rossi"
          className={fieldErrors.recipient ? 'error' : ''}
        />
      </div>

      <div className="field">
        <label>{t('cart.companyName')}</label>
        <input
          type="text"
          value={recipient.company}
          onChange={(e) =>
            setRecipient((prev) => {
              const next = { ...prev, company: e.target.value };
              if (fieldErrors.recipient) {
                setFieldErrors((prevErr) => ({ ...prevErr, recipient: undefined }));
              }
              return next;
            })
          }
          placeholder="Es. Studio Rossi Lighting"
          className={fieldErrors.recipient ? 'error' : ''}
        />
      </div>
    </div>
    {fieldErrors.recipient && <small className="error-text">{fieldErrors.recipient}</small>}

    {/* Riga 2: email + telefono + P.IVA/CF (telefono e P.IVA sono campi corti) */}
    <div className="field-row">
      <div className="field">
        <label>{t('cart.email')}</label>
        <input
          type="email"
          value={recipient.email}
          onChange={(e) =>
            setRecipient((prev) => ({ ...prev, email: e.target.value }))
          }
          placeholder="Es. mario.rossi@example.com"
        />
      </div>

      <div className="field field-sm">
        <label>{t('cart.phone')}</label>
        <input
          type="tel"
          value={recipient.phone}
          onChange={(e) =>
            setRecipient((prev) => ({ ...prev, phone: e.target.value }))
          }
          placeholder="Es. +39 ..."
        />
      </div>

      <div className="field field-sm">
        <label>{t('cart.vat')}</label>
        <input
          type="text"
          value={recipient.vat}
          onChange={(e) =>
            setRecipient((prev) => ({ ...prev, vat: e.target.value }))
          }
          placeholder="Es. IT12345678901"
        />
      </div>
    </div>

    {/* Riga 3: indirizzo */}
    <div className="field-row">
      <div className="field">
        <label>{t('cart.address')}</label>
        <input
          type="text"
          value={recipient.address}
          onChange={(e) =>
            setRecipient((prev) => ({ ...prev, address: e.target.value }))
          }
          placeholder="Via, CAP, Città"
        />
      </div>
    </div>
  </div>
);
  const paymentBlock = canManageQuoteCommercials && (
          <div className="field-group">
            <h4 style={{ margin: '6px 0 10px 0' }}>Pagamento e accettazione</h4>

            <div className="field-row">
              <div className="field">
                <label>Modalita pagamento</label>
                <select
                  value={payment.method}
                  onChange={(e) => setPayment((prev) => ({ ...prev, method: e.target.value }))}
                  className={fieldErrors.paymentMethod ? 'error' : ''}
                >
                  {paymentMethodOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {fieldErrors.paymentMethod && <small className="error-text">{fieldErrors.paymentMethod}</small>}
              </div>
              <div className="field field-sm">
                <label>Scadenza (giorni)</label>
                <input
                  type="number"
                  min="0"
                  max="365"
                  value={payment.dueDays}
                  onChange={(e) => setPayment((prev) => ({ ...prev, dueDays: e.target.value }))}
                  placeholder="Es. 30"
                />
              </div>
              <div className="field field-sm">
                <label>Acconto (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={payment.depositPercent}
                  onChange={(e) => setPayment((prev) => ({ ...prev, depositPercent: e.target.value }))}
                  placeholder="Es. 30"
                />
              </div>
            </div>

            <div className="field-row">
              <div className="field">
                <label>Condizioni pagamento</label>
                <input
                  type="text"
                  value={payment.terms}
                  onChange={(e) => setPayment((prev) => ({ ...prev, terms: e.target.value }))}
                  placeholder="Es. 30 gg data fattura"
                  className={fieldErrors.paymentTerms ? 'error' : ''}
                />
                {fieldErrors.paymentTerms && <small className="error-text">{fieldErrors.paymentTerms}</small>}
              </div>
              <div className="field">
                <label>Tempi di consegna</label>
                <input
                  type="text"
                  value={payment.deliveryTime}
                  onChange={(e) => setPayment((prev) => ({ ...prev, deliveryTime: e.target.value }))}
                  placeholder="Es. 4-6 settimane"
                />
              </div>
            </div>

            <div className="field-row">
              <div className="field">
                <label>Note pagamento</label>
                <textarea
                  rows={2}
                  value={payment.notes}
                  onChange={(e) => setPayment((prev) => ({ ...prev, notes: e.target.value }))}
                />
              </div>
            </div>
          </div>
        );

  // ===================== Riepilogo (solo visualizzazione: i calcoli restano quelli sopra) =====================
  const listTotal = cart.reduce(
    (sum, it) => (it.type === 'adjustment' ? sum : sum + Number(it.unitPrice || 0) * Number(it.quantity || 0)),
    0
  );
  const adjustmentsTotal = cart.reduce((sum, it) => (it.type === 'adjustment' ? sum + getRowTotal(it) : sum), 0);
  const taxableTotal = Number(updateCartTotal());
  const discountsTotal = listTotal - (taxableTotal - adjustmentsTotal); // solo sconti; gli abbuoni sono a parte
  const savedRecipientUnchanged =
    currentOrder && String(currentOrder.recipient?.vat || '') === String(recipient.vat || '');
  const vatRate =
    savedRecipientUnchanged && currentOrder?.vatRate != null
      ? Number(currentOrder.vatRate)
      : computeVatRate({
          recipient,
          customerCountry: currentOrder?.customerCountry || authUser?.country,
          userRole: authUser?.role,
          userCompanyName: authUser?.companyName,
        });
  const vatAmount = (taxableTotal * vatRate) / 100;
  const paymentHasError = !!(fieldErrors.paymentMethod || fieldErrors.paymentTerms);
  const saveLabel = currentOrder ? t('cart.updateOrder') : t('cart.saveOrder');
  const pageTitle = currentOrder ? `${t('cart.editingOrder')} #${currentOrder.orderNumber}` : t('cart.newQuote');

  // ===================== Render =====================
  return (
    <div className="cart-container c2">

      <h2 className="c2-title c2-page-title">
        {pageTitle}
        {currentOrder?.status && <span className="c2-status">{currentOrder.status}</span>}
      </h2>
      <div className="c2-layout">
      <div className="c2-main">

            {/* Controlli ordine */}
      <div className="order-controls" style={{ display: 'grid', gap: 12 }}>
        <div className="field">
          <label>{t('cart.reference')}</label>
          <input
            type="text"
            value={orderReference}
            onChange={(e) => {
              setOrderReference(e.target.value);
              if (fieldErrors.reference) {
                setFieldErrors((prev) => ({ ...prev, reference: undefined }));
              }
            }}
            placeholder={t('cart.referencePlaceholder')}
            className={fieldErrors.reference ? 'error' : ''}
          />
          {fieldErrors.reference && <small className="error-text">{fieldErrors.reference}</small>}
        </div>
        <div className="c2-ref-actions">
          <button type="button" className="c2-btn" onClick={backToOrders}>{t('cart.backToOrders')}</button>
        </div>
        {/* Note generali: sotto il riferimento, sempre visibili */}
        {orderNotesField}


      </div>


      {/* Righe carrello: blocchi per zona (su spazio stretto diventano schede) — la versione precedente è in CartClassic.js (/cart/classic) */}
        <CartLines
          items={cart}
          zoneColorOf={zoneColorOf}
          canEditDiscount={canManageRecipient}
          getRowDiscount={getRowDiscount}
          getNetUnit={getNetUnit}
          getRowTotal={getRowTotal}
          fmtEur={fmtEur}
          fieldErrors={fieldErrors}
          onUpdate={updateCartItem}
          onUpdateNotes={updateCartItemNotes}
          onQuantity={handleQuantityChange}
          onPrice={handlePriceChange}
          onDiscount={handleDiscountChange}
          onMove={moveCartItem}
          onMoveZone={moveZone}
          onRemove={handleRemoveItem}
          onAddRow={addEmptyRow}
          onDuplicate={duplicateRow}
          onEdit={editRow}
          highlightId={highlightId}
          onAddAdjustment={addAdjustmentRow}
          requestZoneName={requestZoneName}
          t={t}
        />
        <div className="cl-total-bar">
          <a className="cl-classic-link" href="/cart/classic" onClick={(e) => { e.preventDefault(); navigate('/cart/classic'); }}>
            {t('cart.classicVersion')}
          </a>
        </div>


      </div>
        <aside className="c2-side">
          <section className="c2-card c2-summary" aria-labelledby="c2-summary-title">
            <h3 id="c2-summary-title">{t('cart.summary')}</h3>
            <dl className="c2-sum">
              <dt>{t('cart.rows')}</dt><dd>{cart.length}</dd>
              <dt>{t('cart.listTotal')}</dt><dd>{fmtEur(listTotal)}</dd>
              {discountsTotal > 0.004 && (<><dt>{t('cart.discounts')}</dt><dd>− {fmtEur(discountsTotal)}</dd></>)}
              {adjustmentsTotal < -0.004 && (<><dt>{t('cart.adjustments')}</dt><dd>− {fmtEur(-adjustmentsTotal)}</dd></>)}
              <dt>{t('cart.taxable')}</dt><dd>{fmtEur(taxableTotal)}</dd>
              <dt>{t('cart.vatLabel')} {vatRate}%</dt><dd>{fmtEur(vatAmount)}</dd>
              <dt className="c2-grand">{t('cart.grandTotal')}</dt><dd className="c2-grand">{fmtEur(taxableTotal + vatAmount)}</dd>
            </dl>
            <button
              type="button"
              className="c2-btn primary c2-btn-block"
              disabled={isSaving || !cart.length}
              title={!currentOrder ? t('cart.saveOrderTooltip') : ''}
              onClick={confirmPlaceOrder}
            >
              {saveLabel}
            </button>
            <button type="button" className="c2-btn danger-text" onClick={handleClearCart}>{t('cart.clearCart')}</button>
          </section>

          {canManageRecipient && (() => {
            const filled = recipientFilled;
            const showForm = !filled || recipientEditing || !!fieldErrors.recipient;
            if (showForm) {
              return (
                <section className="c2-card c2-fields">
                  {recipientBlock}
                  {saveToAddressBookControl}
                  {filled && (
                    <button type="button" className="c2-btn c2-btn-block" onClick={() => setRecipientEditing(false)}>
                      {t('cart.done')}
                    </button>
                  )}
                </section>
              );
            }
            return (
              <section className="c2-card c2-recipient" aria-label={t('cart.recipientQuote')}>
                <div className="c2-card-head">
                  <h3>{t('cart.recipientQuote')}</h3>
                  <button type="button" className="c2-link" onClick={() => setRecipientEditing(true)}>{t('cart.edit')}</button>
                </div>
                <div className="c2-who">
                  <strong>{recipient.company}</strong>
                  <span>{recipient.name}</span>
                  {(recipient.email || recipient.phone) && (
                    <span>{[recipient.email, recipient.phone].filter(Boolean).join(' · ')}</span>
                  )}
                  {recipient.vat && <span>{t('cart.vat')}: {recipient.vat}</span>}
                  {recipient.address && <span>{recipient.address}</span>}
                </div>
                {saveToAddressBookControl}
                {addressBook.length > 0 && (
                  <button type="button" className="c2-link c2-link-start" onClick={() => setShowRecipientPicker(true)}>
                    {t('cart.pickFromAddressBook')}
                  </button>
                )}
              </section>
            );
          })()}

          {canManageQuoteCommercials && (
            <section className="c2-card c2-fields">
              <details open={paymentHasError || !payment.terms}>
                <summary>
                  <span>{t('cart.commercialTerms')}</span>
                  <small>{[paymentMethodOptions.find((o) => o.value === payment.method)?.label, payment.terms].filter(Boolean).join(' · ')}</small>
                </summary>
                {paymentBlock}
              </details>
            </section>
          )}

        </aside>
      </div>

            {/* Modal selezione destinatario da rubrica */}
      {showRecipientPicker && (
        <div
          className="rp-modal-overlay"
          onClick={() => setShowRecipientPicker(false)}
        >
          <div
            className="rp-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rp-modal-header">
              <h3>{t('cart.selectRecipientFromBook')}</h3>
              <button
                type="button"
                className="rp-close-btn"
                onClick={() => setShowRecipientPicker(false)}
              >
                ✖
              </button>
            </div>

            <div className="rp-search-row">
              <input
                type="text"
                placeholder={t('cart.searchRecipientPlaceholder')}
                value={recipientSearch}
                onChange={(e) => setRecipientSearch(e.target.value)}
              />
            </div>

            <div className="rp-list">
              {filteredAddressBook.length === 0 && (
                <p className="rp-empty">{t('cart.noContactFound')}</p>
              )}

              {filteredAddressBook.map((c) => (
                <div
                  key={c.id}
                  className="rp-row"
                  onClick={() => handlePickRecipient(c)}
                >
                  <div className="rp-main">
                    <div className="rp-name">{c.name || '-'}</div>
                    <div className="rp-company">{c.company || '—'}</div>
                  </div>
                  <div className="rp-meta">
                    <div>{c.email || '—'}</div>
                    <div>{c.phone || ''}</div>
                    <div>{c.vat || ''}</div>
                  </div>
                  <button
                    type="button"
                    className="rp-use-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePickRecipient(c);
                    }}
                  >
                    {t('cart.use')}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <style>{`
            .rp-modal-overlay {
              position: fixed;
              inset: 0;
              background: rgba(0,0,0,0.35);
              display: flex;
              justify-content: center;
              align-items: center;
              z-index: 2500;
            }

            .rp-modal {
              background: #fff;
              border-radius: 10px;
              width: 520px;
              max-width: 95vw;
              max-height: 80vh;
              padding: 14px 16px;
              box-shadow: 0 10px 30px rgba(0,0,0,0.25);
              display: flex;
              flex-direction: column;
            }

            .rp-modal-header {
              display: flex;
              align-items: center;
              justify-content: space-between;
              margin-bottom: 8px;
            }

            .rp-close-btn {
              background: none;
              border: none;
              font-size: 16px;
              cursor: pointer;
            }

            .rp-search-row input {
              width: 100%;
              padding: 6px 8px;
              border-radius: 6px;
              border: 1px solid #ccc;
              font-size: 13px;
              margin-bottom: 10px;
            }

            .rp-list {
              overflow-y: auto;
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 6px;
            }

            .rp-empty {
              font-style: italic;
              color: #777;
            }

            .rp-row {
              display: grid;
              grid-template-columns: 2fr 3fr auto;
              gap: 8px;
              align-items: center;
              padding: 8px 10px;
              border-radius: 6px;
              border: 1px solid #e0e0e0;
              background: #fafafa;
              cursor: pointer;
              transition: background 0.15s, border-color 0.15s;
            }

            .rp-row:hover {
              background: #f0f7ff;
              border-color: #1976d2;
            }

            .rp-main {
              display: flex;
              flex-direction: column;
            }

            .rp-name {
              font-weight: 600;
              color: #222;
              font-size: 13px;
            }

            .rp-company {
              font-size: 11px;
              color: #666;
            }

            .rp-meta {
              font-size: 11px;
              color: #333;
              display: flex;
              flex-direction: column;
              gap: 2px;
            }

            .rp-use-btn {
              padding: 4px 8px;
              border-radius: 6px;
              border: none;
              background: #1976d2;
              color: #fff;
              font-size: 12px;
              cursor: pointer;
              white-space: nowrap;
            }
          `}</style>
        </div>
      )}


      {/* Barra fissa su telefono: totale (IVA inclusa) + Salva — gli altri comandi sono nella pagina */}
      <div className="c2-mobile-bar">
        <div className="c2-mobile-bar-total">
          <span>{t('cart.grandTotal')}</span>
          <strong>{fmtEur(taxableTotal + vatAmount)}</strong>
        </div>
        <button
          type="button"
          className="c2-btn primary"
          disabled={isSaving || !cart.length}
          title={!currentOrder ? t('cart.saveOrderTooltip') : ''}
          onClick={confirmPlaceOrder}
        >
          {saveLabel}
        </button>
      </div>

      {dialog && <CartDialog config={dialog} onClose={() => setDialog(null)} cancelLabel={t('cart.cancel')} />}

      {/* Messaggio di feedback */}
      {message && (
        <div className="message">
          <p>{message}</p>
          <button onClick={() => setMessage('')}>{t('cart.ok')}</button>
        </div>
      )}

      {/* Stili inline per sticky bar + padding-bottom per non coprire contenuto */}
      <style>{`
        /* SPAZIO PER LA STICKY BAR */
.cart-container { 
  padding-bottom: 96px;
}

    /* RIGA ABBUONO / ADJUSTMENT */
    .row-adjustment td { 
      background: #fafafa;
    }

.error {
  border-color: #c62828 !important;
  background: #fff6f6;
}

.error-text {
  color: #c62828;
  font-size: 12px;
  margin-top: 4px;
  display: inline-block;
}

/* BARRA STICKY IN FONDO */
.cart-sticky-actions {
  position: sticky;
  bottom: 0;
  z-index: 5;
  background: #fff;
  border-top: 1px solid #e6e6e6;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-shadow: 0 -4px 12px rgba(0,0,0,0.06);
}

.cart-sticky-actions .left-actions,
.cart-sticky-actions .right-actions {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

/* BUTTON DESIGN */
.cart-button {
  width: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #d0d0d0;
  background: #f8f8f8;
  cursor: pointer;
  transition: 0.2s ease;
}

.cart-button.primary {
  background: #1976d2;
  border-color: #1976d2;
  color: #fff;
}

.cart-button.primary:hover {
  background: #0f5bad;
}

.cart-button.danger {
  background: #ffecec;
  border-color: #f5b5b5;
  color: #c62828;
}

.cart-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cart-button.secondary {
  background: #f8f8f8;
  border-color: #d0d0d0;
  color: #333;          /* testo ben leggibile */
}

.cart-button.secondary:hover {
  background: #eaeaea;
}


/* ================================ */
/*     SEZIONE DESTINATARIO         */
/* ================================ */

.field-group {
  margin-top: 18px;
}

/* RIGHE CON 2 CAMPI ALLINEATI */
.field-row {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 12px;
}

/* OGNI CAMPO */
.field-row .field {
  flex: 1;
  min-width: 260px;
  display: flex;
  flex-direction: column;
}

/* LABEL UNIFORMI */
.field-row .field label {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 4px;
  color: #333;
}

/* INPUT: ALTEZZA, BORDI, COLORE, FOCUS */
.field-row .field input {
  width: 100%;
  height: 38px;
  padding: 6px 10px;
  font-size: 14px;
  border: 1px solid #cccccc;
  border-radius: 6px;
  background: #fff;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.field-row .field input:focus {
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.25);
}

/* CAMPI FULL-WIDTH SOTTO (indirizzo, p.iva) */
.field-group .field {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
}

.field-group .field label {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 4px;
}

.field-group .field input {
  height: 38px;
  padding: 6px 10px;
  font-size: 14px;
  border: 1px solid #cccccc;
  border-radius: 6px;
  background: #fff;
  transition: 0.2s ease;
}

.field-group .field input:focus {
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.25);
}

/* ================================ */
/*         SCONTO INPUT             */
/* ================================ */
.discount-input {
  width: 60px;
  height: 32px;
  padding: 4px;
  font-size: 13px;
  text-align: right;
  border: 1px solid #cccccc;
  border-radius: 4px;
  outline: none;
  transition: 0.2s;
}

.discount-input:focus {
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.25);
}


      `}</style>
    </div>
  );
};

export default Cart;
